import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { statusInstrumentoOptions, generateQRCode } from '../mock';
import { useToast } from '../hooks/use-toast';
import { Edit, QrCode, DollarSign } from 'lucide-react';

const EditInstrumentModal = ({ isOpen, onClose, instrument, onEdit }) => {
  const [formData, setFormData] = useState(instrument);
  const [showQRCode, setShowQRCode] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    setFormData(instrument);
  }, [instrument]);

  const parseFlexibleDate = (dateInput) => {
    if (!dateInput) return null;
    
    // Try to parse the date in various formats
    try {
      // If it's already in YYYY-MM-DD format
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
        return dateInput;
      }
      
      // If it's in DD/MM/YYYY format
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateInput)) {
        const [day, month, year] = dateInput.split('/');
        return `${year}-${month}-${day}`;
      }
      
      // If it's in MM/DD/YYYY format
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateInput)) {
        const date = new Date(dateInput);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
      }
      
      // Try to create a date object and format it
      const date = new Date(dateInput);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      
      // If all else fails, return the input as is
      return dateInput;
    } catch (error) {
      return dateInput;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Parse the date flexibly
    const parsedDate = parseFlexibleDate(formData.proximaCalibracao);

    // Calculate status based on dates
    // Data retroativa (passado) = vencido
    // Data atual ou posterior = em dia
    let status = 'em_estoque';
    if (parsedDate) {
      try {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Zerar horas para comparação apenas de data
        
        const expiryDate = new Date(parsedDate);
        expiryDate.setHours(0, 0, 0, 0);
        
        if (!isNaN(expiryDate.getTime())) {
          // Se a data de vencimento for ANTERIOR a hoje = vencido
          // Se a data de vencimento for IGUAL ou POSTERIOR a hoje = em dia
          status = expiryDate < today ? 'vencido' : 'em_estoque';
        }
      } catch (error) {
        status = 'em_estoque';
      }
    }

    onEdit({ 
      ...formData, 
      proximaCalibracao: parsedDate || formData.proximaCalibracao,
      status,
      custoCalibracao: parseFloat(formData.custoCalibracao) || 0,
      custoManutencao: parseFloat(formData.custoManutencao) || 0,
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Edit className="w-6 h-6 text-orange-600" />
            Editar Instrumento
          </DialogTitle>
          <DialogDescription>
            Atualize os dados do instrumento. Todos os campos são opcionais.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="nome">Nome do Instrumento</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="marca">Marca</Label>
              <Input
                id="marca"
                value={formData.marca}
                onChange={(e) => setFormData({ ...formData, marca: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="tipo">Tipo</Label>
              <Input
                id="tipo"
                value={formData.tipo}
                onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                placeholder="Ex: Paquímetro, Micrômetro, Balança"
              />
            </div>
            <div>
              <Label htmlFor="faixaMedicao">Faixa de Medição</Label>
              <Input
                id="faixaMedicao"
                value={formData.faixaMedicao || ''}
                onChange={(e) => setFormData({ ...formData, faixaMedicao: e.target.value })}
                placeholder="Ex: 0-150mm, 0-100 bar"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="proximaCalibracao">Data de Vencimento da Calibração</Label>
              <Input
                id="proximaCalibracao"
                value={formData.proximaCalibracao}
                onChange={(e) => setFormData({ ...formData, proximaCalibracao: e.target.value })}
                placeholder="Ex: 2025-12-31 ou 31/12/2025"
              />
              <p className="text-xs text-slate-500 mt-1">
                Aceita formatos: YYYY-MM-DD, DD/MM/YYYY ou outros formatos de data
              </p>
            </div>
            <div>
              <Label htmlFor="responsavel">Responsável</Label>
              <Input
                id="responsavel"
                value={formData.responsavel}
                onChange={(e) => setFormData({ ...formData, responsavel: e.target.value })}
                placeholder="Ex: Anderson, João Silva"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="setor">Setor</Label>
              <Input
                id="setor"
                value={formData.setor || ''}
                onChange={(e) => setFormData({ ...formData, setor: e.target.value })}
                placeholder="Ex: Produção, Qualidade, Laboratório"
              />
            </div>
            <div>
              <Label htmlFor="statusInstrumento">Status do Instrumento</Label>
              <Select
                value={formData.statusInstrumento || ''}
                onValueChange={(value) => setFormData({ ...formData, statusInstrumento: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o status" />
                </SelectTrigger>
                <SelectContent>
                  {statusInstrumentoOptions.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Orçamento Section */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4 space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-5 h-5 text-orange-600" />
              <h4 className="font-semibold text-orange-900">Informações de Orçamento</h4>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="orcamentoNumero">Orçamento Nº</Label>
                <Input
                  id="orcamentoNumero"
                  value={formData.orcamentoNumero || ''}
                  onChange={(e) => setFormData({ ...formData, orcamentoNumero: e.target.value })}
                  placeholder="Ex: ORC-2024-001"
                />
              </div>
              <div>
                <Label htmlFor="custoCalibracao">Custo de Calibração (R$)</Label>
                <Input
                  id="custoCalibracao"
                  type="number"
                  step="0.01"
                  value={formData.custoCalibracao || ''}
                  onChange={(e) => setFormData({ ...formData, custoCalibracao: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label htmlFor="custoManutencao">Custo de Manutenção (R$)</Label>
                <Input
                  id="custoManutencao"
                  type="number"
                  step="0.01"
                  value={formData.custoManutencao || ''}
                  onChange={(e) => setFormData({ ...formData, custoManutencao: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* QR Code Section */}
          <div className="bg-gradient-to-r from-slate-50 to-gray-50 border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <QrCode className="w-5 h-5 text-slate-700" />
              <h4 className="font-semibold text-slate-900">QR Code do Certificado</h4>
            </div>
            <div className="space-y-3">
              <div>
                <Label htmlFor="qrCodeUrl">URL do Certificado</Label>
                <Input
                  id="qrCodeUrl"
                  value={formData.qrCodeUrl || ''}
                  onChange={(e) => setFormData({ ...formData, qrCodeUrl: e.target.value })}
                  placeholder="https://exemplo.com/certificado/CERT-2024-001"
                />
              </div>
              {formData.qrCodeUrl && (
                <div className="flex gap-3 items-start">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowQRCode(!showQRCode)}
                    className="border-orange-500 text-orange-600 hover:bg-orange-50"
                  >
                    <QrCode className="w-4 h-4 mr-2" />
                    {showQRCode ? 'Ocultar QR Code' : 'Visualizar QR Code'}
                  </Button>
                  {showQRCode && (
                    <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                      <img
                        src={generateQRCode(formData.qrCodeUrl)}
                        alt="QR Code"
                        className="w-32 h-32"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="observacoes">Observações</Label>
            <Textarea
              id="observacoes"
              value={formData.observacoes}
              onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700">
              Salvar Alterações
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditInstrumentModal;